import { deriveCloudBackupCredentials, encryptedBackupSize, type BackupBundle, type EncryptedBackup } from './backup.js';
import { cloudBackupResponseSchema } from '@wolhasudang/shared';
import { platform } from './platform.js';

export async function uploadCloudBackup(bundle: BackupBundle, fetcher: typeof fetch = fetch): Promise<{ updatedAt: number; size: number }> {
  const response = await fetcher(platform.apiUrl(`/api/v1/backup/${encodeURIComponent(bundle.envelope.lookupKey)}`), {
    method: 'PUT', headers: { Authorization: `Backup ${bundle.accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      schemaVersion: bundle.envelope.schemaVersion, salt: bundle.envelope.salt, ciphertext: bundle.envelope.ciphertext,
      nonce: bundle.envelope.nonce, checksum: bundle.envelope.checksum, size: encryptedBackupSize(bundle.envelope),
    }),
  });
  if (!response.ok) throw new Error(await cloudError(response));
  return await response.json() as { updatedAt: number; size: number };
}

export async function downloadCloudBackup(recoveryCode: string, fetcher: typeof fetch = fetch): Promise<EncryptedBackup> {
  const credentials = await deriveCloudBackupCredentials(recoveryCode);
  const response = await fetcher(platform.apiUrl(`/api/v1/backup/${encodeURIComponent(credentials.lookupKey)}`), { headers: { Authorization: `Backup ${credentials.accessToken}` } });
  if (!response.ok) throw new Error(await cloudError(response));
  const body = cloudBackupResponseSchema.parse(await response.json());
  if (body.salt !== credentials.salt) throw new Error('CLOUD_BACKUP_METADATA_MISMATCH');
  return { schemaVersion: body.schemaVersion, salt: body.salt, lookupKey: credentials.lookupKey, nonce: body.nonce, ciphertext: body.ciphertext, checksum: body.checksum };
}

export async function deleteCloudBackup(recoveryCode: string, fetcher: typeof fetch = fetch): Promise<void> {
  const credentials = await deriveCloudBackupCredentials(recoveryCode);
  const response = await fetcher(platform.apiUrl(`/api/v1/backup/${encodeURIComponent(credentials.lookupKey)}`), { method: 'DELETE', headers: { Authorization: `Backup ${credentials.accessToken}` } });
  if (!response.ok) throw new Error(await cloudError(response));
}

async function cloudError(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: { code?: string } };
    return body.error?.code ?? `CLOUD_BACKUP_HTTP_${response.status}`;
  } catch { return `CLOUD_BACKUP_HTTP_${response.status}`; }
}
